import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Button, 
  MenuItem, 
  Select, 
  AppBar, 
  Toolbar, 
  Box, 
  Grid, 
  Typography, 
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup
} from '@mui/material';
import { getFleets, getFleet, getPrice, getOperatorData, createCheckoutSession, checkVehicleAvailable, getCoupon, getReservationSurcharges, getAvailableVehicleTypes } from '../api';
import DOMPurify from 'dompurify';
import LoginDialog from '../components/LoginDialog';
import DateRangePicker from '../components/DateRangePicker';
import RentalDetails from '../components/RentalDetails';

const ReservationPage = () => {
  const navigate = useNavigate(); 
  const loc = useLocation();
  const [fleets, setFleets] = useState([]); 
  const [vehicleTypes, setVehicleTypes] = useState([]); 
  const [availableVehicleTypes, setAvailableVehicleTypes] = useState([]); 
  const [isLoadingVehicleTypes, setIsLoadingVehicleTypes] = useState(false);
  const [vehicleTypeError, setVehicleTypeError] = useState(null);
  const [vehicleImage, setVehicleImage] = useState([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState(''); 
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 800);
  const [selectedVehicle, setSelectedVehicle] = useState(null); 
  const [dateRangeSelected, setDateRangeSelected] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedVehicleName, setSelectedVehicleName] = useState([]);
  const [priceDetails, setPriceDetails] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [couponCode, setCouponCode] = useState('');
  const [couponId, setCouponId] = useState(null);
  const [couponDescription, setCouponDescription] = useState('');
  const [hasCoupon, setHasCoupon] = useState(false);
  const [couponPercent, setCouponPercent] = useState(null);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [couponError, setCouponError] = useState('');
  
  // New state variables for note functionality
  const [noteText, setNoteText] = useState('');
  const [hasNote, setHasNote] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  // Add-on state variables
  const [addons, setAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false);
  
  // Quantity state - tracks selected quantity for each vehicle type
  const [vehicleQuantities, setVehicleQuantities] = useState({});
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  const handleViewPrices = useCallback(async (overrideCouponId = null) => {
    try {
      if (!selectedVehicleType) {
        alert("Please select a vehicle type");
        return;
      }
      
      if (!dateRangeSelected) {
        // Don't fetch price if no date range has been selected
        return;
      }
      
      const fleet = JSON.parse(localStorage.getItem('fleet'));
      const start_date = selectedStartDate.toISOString();
      setStartDate(start_date);
      const end_date = selectedEndDate.toISOString();
      setEndDate(end_date);

      // Use the override couponId if provided, otherwise use the state value
      const couponIdToUse = overrideCouponId !== null ? overrideCouponId : couponId;
      
      // Fetch price details with couponId if available and include selected addons and quantity
      const response = await getPrice(fleet.id, start_date, end_date, selectedVehicleType, couponIdToUse, selectedAddons, selectedQuantity);

      // If coupon was rejected (expired or max uses reached), show inline error and allow retry
      if (response.coupon_rejected) {
        setCouponDescription('');
        setCouponPercent(null);
        setHasCoupon(false);
        setSubmitDisabled(false);
        setCouponError('This coupon has expired or reached its maximum number of uses.');
        // Clear couponId last to avoid re-triggering useEffect chain before other state is set
        setTimeout(() => setCouponId(null), 0);
      }

      setPriceDetails(response);
      
      // Now that we have price details with fleet_fare_id, fetch the add-ons
      if (response && response.fleet_fare_id) {
        const operatorParam = new URLSearchParams(loc.search).get('operator');
        getReservationSurcharges(operatorParam, response.fleet_fare_id)
          .then((surcharges) => {
            // The response is already an array of surcharges
            // Filter surcharges to get addons (surcharge_type === 'reservation_addon')
            const addonSurcharges = Array.isArray(surcharges) ? 
              surcharges.filter(surcharge => surcharge.surcharge_type === 'reservation_addon') : 
              [];
            setAddons(addonSurcharges);
          })
          .catch((error) => console.error('Failed to load surcharges:', error));
      }
      
    } catch (error) {
      console.error("Error fetching price details:", error);
    }
  }, [selectedVehicleType, selectedStartDate, selectedEndDate, dateRangeSelected, couponId, selectedAddons, selectedQuantity, loc.search]);

  useEffect(() => {
    const handleResize = () => {
      const currentIsMobile = window.innerWidth <= 800;
      setIsMobile(currentIsMobile); 
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);  
  // Update document title with fleet account name
  useEffect(() => {
    const fleet = JSON.parse(localStorage.getItem('fleet')) || {};
    if (fleet.account_name) {
      document.title = fleet.account_name;
    } else {
      document.title = 'Roamr Reservations'; // fallback title
    }
  }, []); // Run only once on mount

  useEffect(() => {
    const queryParams = new URLSearchParams(loc.search);
    const fleetParam = queryParams.get('fleet');
    const operatorParam = queryParams.get('operator');

    if (operatorParam) {
      getOperatorData(operatorParam).then((operatorResponse) => {
        localStorage.setItem('operator', JSON.stringify(operatorResponse))
      });
      getFleets(operatorParam)
        .then((data) => setFleets(data.fleets))
        .catch((error) => console.error('Failed to load fleets:', error));
      
      // Get the fleet data if fleetParam is available
      if (fleetParam) {
        getFleet(fleetParam)
          .then((fleetData) => {
            const fleet = fleetData?.fleet;
            if (fleet) {
              localStorage.setItem('fleet', JSON.stringify(fleet));
              setVehicleTypes(fleet.vehicle_types || []);
            }
          })
          .catch((error) => console.error('Failed to fetch fleet data:', error));
      }
    }

    if (selectedStartDate && selectedEndDate && selectedVehicleType) {
      handleViewPrices();
    }
  }, [navigate, loc.search, selectedStartDate, selectedEndDate, selectedVehicleType, handleViewPrices]);



  const handleFleetChange = async (event) => {
    const selectedFleetId = event.target.value;
    try {
      const fleetData = await getFleet(selectedFleetId);
      const fleet = fleetData?.fleet;
      if (fleet) {
        localStorage.setItem('fleet', JSON.stringify(fleet));
        setVehicleTypes(fleet.vehicle_types || []);
      }
    } catch (error) {
      console.error('Failed to fetch and set fleet data:', error);
    }
  };

  const handleDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setSelectedStartDate(newStartDate);
    setSelectedEndDate(newEndDate);
    setDateRangeSelected(true);
    // Clear available vehicles when dates change - user must click Find Vehicles
    setAvailableVehicleTypes([]);
    setVehicleTypeError(null);
    // Reset the entire flow - clear vehicle selection and all related state
    setSelectedVehicleType('');
    setSelectedVehicle(null);
    setVehicleImage([]);
    setSelectedVehicleName([]);
    setPriceDetails(null);
    setSelectedQuantity(1);
    // Clear add-ons and coupon
    setAddons([]);
    setSelectedAddons([]);
    setCouponCode('');
    setCouponId(null);
    setCouponDescription('');
    setHasCoupon(false);
    setCouponPercent(null);
    setSubmitDisabled(false);
    // Clear note
    setNoteText('');
    setHasNote(false);
    setNoteContent('');
  };

  const handleFindVehicles = async () => {
    const fleet = JSON.parse(localStorage.getItem('fleet'));
    
    if (!selectedStartDate || !selectedEndDate) {
      alert('Please select a date range first');
      return;
    }
    
    if (fleet && fleet.id) {
      setIsLoadingVehicleTypes(true);
      setVehicleTypeError(null);
      setAvailableVehicleTypes([]);
      // Clear current selection
      setSelectedVehicleType('');
      setSelectedVehicle(null);
      setVehicleImage([]);
      setSelectedVehicleName([]);
      setPriceDetails(null);
      
      try {
        const availableTypes = await getAvailableVehicleTypes(
          fleet.id,
          selectedStartDate.toISOString(),
          selectedEndDate.toISOString()
        );
        
        console.log('handleFindVehicles - Available types received:', availableTypes);
        
        if (availableTypes && availableTypes.length > 0) {
          setAvailableVehicleTypes(availableTypes);
          setVehicleTypeError(null);
        } else {
          setAvailableVehicleTypes([]);
          setVehicleTypeError('No vehicles available for the selected dates. Please try a different date range or contact the operator.');
        }
      } catch (error) {
        console.error('Failed to fetch available vehicle types:', error);
        setVehicleTypeError('Unable to check vehicle availability. Please try again.');
        setAvailableVehicleTypes([]);
      } finally {
        setIsLoadingVehicleTypes(false);
      }
    }
  };

  const handleVehicleSelection = (vehicleTypeId) => {
    setSelectedVehicleType(vehicleTypeId);
    const selectedVehicle = vehicleTypes.find(vehicle => vehicle.vehicle_type_id === vehicleTypeId);
    if (selectedVehicle) {
      const vehicle_image = selectedVehicle.features.vehicle_image;
      setVehicleImage(vehicle_image);
      setSelectedVehicle(selectedVehicle); 
      setSelectedVehicleName(selectedVehicle.vehicle_type_name);
      
      // Set the selected quantity from the dropdown (default to 1 if not set)
      const quantity = vehicleQuantities[vehicleTypeId] || 1;
      setSelectedQuantity(quantity);
    }
  };  
  
  const handleReserveClick = async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); 

    if (selectedStartDate < today) {
      alert("The start date cannot be in the past. Please select a valid date range.");
      return;
    }

    try {
      await checkVehicleAvailable(selectedVehicleType, fleet.id, startDate, endDate);
      setIsLoginDialogOpen(true); 
    } catch (error) {
      alert(error.message);
    }
  };
  
  // Handle add-on selection/deselection
  const handleAddonChange = (addonId) => {
    setSelectedAddons(prev => {
      const newSelectedAddons = prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId];
      
      return newSelectedAddons;
    });
  };

  const handleSubmitCoupon = async () => {
    setCouponError('');
    try {
      const response = await getCoupon(fleet.id, couponCode);

      if (response && response.ok) {
        const data = await response.json();
        if (Array.isArray(data) && data.length === 0) {
          setCouponError('No coupon found for the provided code.');
        } else if (data && data[0].description && data[0].discount_pct) {
          const newCouponId = data[0].id;
          setCouponDescription(data[0].description);
          setCouponPercent(data[0].discount_pct);
          setCouponId(newCouponId);
          setHasCoupon(true);
          setSubmitDisabled(true);
          // Pass the couponId directly to handleViewPrices to avoid state timing issues
          handleViewPrices(newCouponId);
        } else {
          setCouponError('This coupon code is not valid.');
        }
      } else {
        setCouponError('Unable to validate coupon. Please try again.');
      }
    } catch (error) {
      console.error('Coupon submission error:', error);
      setCouponError('Unable to validate coupon. Please try again.');
    }
  };  
  const fleet = JSON.parse(localStorage.getItem('fleet')) || {};
  const requiredParams = new URLSearchParams(loc.search);
  const hasRequiredParams = requiredParams.get('operator') && requiredParams.get('fleet');

  if (!hasRequiredParams) {
    return (
      <div style={{
        backgroundColor: '#f7f9fc',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '500px',
          padding: '40px',
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>
          <h1 style={{ fontSize: '24px', color: '#333', marginBottom: '16px' }}>
            Reservation Page Not Available
          </h1>
          <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.6', marginBottom: '24px' }}>
            This reservation page requires a valid booking link from your rental provider.
            Please check the link you were given or contact your rental provider for assistance.
          </p>
          <p style={{ fontSize: '14px', color: '#999' }}>
            If you believe this is an error, please verify the URL includes the required operator and fleet information.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#f7f9fc',
      minHeight: '100vh',
      backgroundImage: fleet.reservation_hero_img ? `url(${fleet.reservation_hero_img})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      backgroundRepeat: 'no-repeat'
    }}>
      <AppBar position="static" style={{ backgroundColor: fleet.menu_bkg_color || '#1a73e8' }}>
        <Toolbar>
          <Box
            component="img"
            src={fleet.web_logo || 'default_logo.png'}
            alt="Menu Logo"
            sx={{
              backgroundColor: fleet.web_bkg_color || '#fff',
              padding: '8px',
              borderRadius: '8px',
              height: '125px',
              objectFit: 'contain',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          />
        </Toolbar>
      </AppBar>
      <Box mt={4} mx="auto" width={isMobile ? "90%" : "100%"} maxWidth="1400px" pt={4} px={0}>
        <Grid container spacing={isMobile ? 1 : 2}>
          <Grid 
            item
            xs={12}
            md={4}
            lg={3}
            style={{ 
              padding: isMobile ? 0 : '0 10px 10px 0',
              position: 'relative',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Fleet Selector */}
            {fleets.length > 1 ? (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                  padding: isMobile ? '9px' : '15px',
                  position: 'relative',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  borderRadius: '10px',
                  overflow: 'visible',
                  marginBottom: '1.5rem',
                  border: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                  Select Fleet Location
                </Typography>
                <Select
                  value={fleets.some(f => f.id === fleet.id) ? fleet.id : ''}
                  onChange={handleFleetChange}
                  disabled={!fleet.id}
                  sx={{ 
                    width: '100%',
                    bgcolor: 'white', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  {fleets.map(fleet => (
                    <MenuItem key={fleet.id} value={fleet.id}>
                      {fleet.account_name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            ) : fleets.length === 1 ? (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                  padding: isMobile ? '9px' : '15px',
                  position: 'relative',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  borderRadius: '10px',
                  overflow: 'visible',
                  marginBottom: '1.5rem',
                  border: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#333',
                    textAlign: 'center'
                  }}
                >
                  {fleet.account_name}
                </Typography>
              </Box>
            ) : null}

            {/* Date Range Picker */}
            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                margin: 0,
                padding: isMobile ? '9px' : '15px',
                position: 'relative',
                backgroundColor: '#fff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                borderRadius: '10px',
                overflow: 'visible',
                marginBottom: '1.5rem',
                border: '1px solid rgba(0,0,0,0.05)'
              }}
            >
              <DateRangePicker
                startDate={selectedStartDate}
                endDate={selectedEndDate}
                onChange={handleDateRangeChange}
                minDate={new Date()}
              />
              
              {/* Find Vehicles Button */}
              <Button
                onClick={handleFindVehicles}
                variant="contained"
                disabled={!dateRangeSelected || isLoadingVehicleTypes}
                sx={{
                  mt: 2,
                  width: '100%',
                  bgcolor: '#00B894',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,184,148,0.3)',
                  fontSize: isMobile ? '15px' : '16px',
                  fontWeight: 600,
                  py: isMobile ? 1.8 : 1.5,
                  minHeight: isMobile ? '50px' : 'auto',
                  '&:hover': {
                    backgroundColor: '#00A983',
                  },
                  '&:disabled': {
                    backgroundColor: '#cccccc',
                    color: '#666',
                  }
                }}
              >
                {isLoadingVehicleTypes ? 'Searching...' : 'Find Available Vehicles'}
              </Button>
            </Box>
            
            <div style={{ height: '20px' }} />
            
            {/* Add-ons Section - Only show when vehicle is selected and dates are set */}
            {selectedVehicleType && dateRangeSelected && addons.length > 0 && (
              <Box 
                sx={{ 
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                  padding: isMobile ? '9px' : '15px',
                  position: 'relative',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  borderRadius: '10px',
                  overflow: 'visible',
                  marginBottom: '1.5rem',
                  border: '1px solid rgba(0,0,0,0.05)',
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-start',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>Add-ons</Typography>
                
                {addons.map((addon) => (
                  <FormGroup sx={{ width: '100%' }} key={addon.id}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={selectedAddons.includes(addon.id)}
                          onChange={() => handleAddonChange(addon.id)}
                          sx={{
                            color: '#00B894',
                            '&.Mui-checked': {
                              color: '#00B894',
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {addon.description} - {addon.percent ? `${addon.percent * 100}%` : `$${(addon.amount / 100).toFixed(2)} ${addon.charge_type === 'daily' ? '(daily)' : ''}`}
                          </Typography>
                        </Box>
                      }
                    />
                  </FormGroup>
                ))}
              </Box>
            )}
            
            <div style={{ height: '20px' }} />
            
            {/* Coupon Section - Only show when vehicle is selected and dates are set */}
            {selectedVehicleType && dateRangeSelected && (
              <Box 
                sx={{ 
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                  padding: isMobile ? '9px' : '15px',
                  position: 'relative',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  borderRadius: '10px',
                  overflow: 'visible',
                  marginBottom: '1.5rem',
                  border: '1px solid rgba(0,0,0,0.05)',
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-start',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>Enter Coupon</Typography>
              <TextField
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value);
                  setCouponError('');
                  if (submitDisabled && !hasCoupon) {
                    setSubmitDisabled(false);
                  }
                }}
                placeholder="Enter your coupon code"
                fullWidth
                sx={{ 
                  marginTop: 1, 
                  marginBottom: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&.Mui-focused fieldset': {
                      borderColor: '#00B894'
                    }
                  }
                }}
              />
              <Button
                onClick={handleSubmitCoupon}
                variant="contained"
                color="primary"
                disabled={submitDisabled}
                sx={{ 
                  mt: 2,
                  bgcolor: '#00B894',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,184,148,0.3)',
                  '&:hover': {
                    backgroundColor: '#00A983',
                  },
                  '&:disabled': {
                    backgroundColor: '#cccccc',
                  }
                }}
              >
                Apply Coupon
              </Button>

              {couponError && (
                <Typography
                  variant="body1"
                  sx={{
                    mt: 2,
                    color: '#d32f2f',
                    bgcolor: 'rgba(211,47,47,0.1)',
                    p: 1,
                    borderRadius: '6px',
                    width: 'calc(100% - 8px)',
                    fontWeight: 500,
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                  }}
                >
                  {couponError}
                </Typography>
              )}
              {hasCoupon && !couponError && (
                <Typography
                  variant="body1"
                  sx={{
                    mt: 2,
                    color: "#00B894",
                    bgcolor: 'rgba(0,184,148,0.1)',
                    p: 1,
                    borderRadius: '6px',
                    width: 'calc(100% - 8px)',
                    fontWeight: 500,
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                  }}
                >
                  Coupon added: {couponDescription}
                </Typography>
              )}
              </Box>
            )}
            
            <div style={{ height: '20px' }} />
            
            {/* Note Entry Box - Only show when vehicle is selected and dates are set */}
            {selectedVehicleType && dateRangeSelected && (
              <Box 
                sx={{ 
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                  padding: isMobile ? '9px' : '15px',
                  position: 'relative',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  borderRadius: '10px',
                  overflow: 'visible',
                  marginBottom: '1.5rem',
                  border: '1px solid rgba(0,0,0,0.05)',
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-start',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                  {selectedVehicle?.features?.notes_title || 'Add a Note'}
                </Typography>
              <TextField
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder={selectedVehicle?.features?.notes_placeholder || "Add any special requests or notes here"}
                multiline
                minRows={2}
                maxRows={10}
                fullWidth
                sx={{ 
                  marginTop: 1, 
                  marginBottom: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&.Mui-focused fieldset': {
                      borderColor: '#00B894'
                    }
                  },
                  '& .MuiInputBase-root': {
                    minHeight: '56px',
                    alignItems: 'flex-start'
                  },
                  '& .MuiOutlinedInput-input': {
                    overflow: 'auto',
                    maxHeight: 'unset',
                    lineHeight: 1.5
                  }
                }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'row', mt: 2 }}>
                <Button
                  onClick={() => {
                    if (noteText.trim()) {
                      setNoteContent(noteText.trim());
                      setHasNote(true);
                    }
                  }}
                  variant="contained"
                  color="primary"
                  disabled={noteText.trim() === ''} // Only disable when the text field is empty
                  sx={{ 
                    bgcolor: '#00B894',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,184,148,0.3)',
                    '&:hover': {
                      backgroundColor: '#00A983',
                    },
                    '&:disabled': {
                      backgroundColor: '#cccccc',
                    }
                  }}
                >
                  {hasNote ? 'Update Note' : 'Add Note'}
                </Button>
                
                {hasNote && (
                  <Button
                    onClick={() => {
                      setNoteContent('');
                      setNoteText('');
                      setHasNote(false);
                    }}
                    variant="outlined"
                    sx={{ 
                      ml: 2,
                      color: '#ff5252',
                      borderColor: '#ff5252',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: 'rgba(255,82,82,0.08)',
                        borderColor: '#ff3232',
                      }
                    }}
                  >
                    Remove Note
                  </Button>
                )}
              </Box>
              
              {hasNote && (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mt: 2, 
                    color: "#00B894", 
                    bgcolor: 'rgba(0,184,148,0.1)', 
                    p: 1,
                    borderRadius: '6px', 
                    width: 'calc(100% - 8px)',
                    fontWeight: 500,
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                  }}
                >
                  Note added to rental
                </Typography>
              )}
              </Box>
            )}
          </Grid>
          
          <Grid 
            item
            xs={12}
            md={8}
            lg={9}
            style={{ 
              padding: isMobile ? 0 : '0 0 0 32px',
              position: 'relative',
              zIndex: 900
            }}
          >
            {/* Show instructions when no search has been done yet */}
            {availableVehicleTypes.length === 0 && !vehicleTypeError && !selectedVehicleType ? (
              <Box
                sx={{ 
                  width: '100%',
                  maxWidth: '700px',
                  margin: '0 auto',
                  backgroundColor: '#ffffff',
                  borderRadius: '10px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  p: isMobile ? 1 : 4,
                  border: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#333', mb: 3, textAlign: 'center' }}>
                  Welcome to {fleet.account_name || 'Vehicle Reservations'}
                </Typography>
                <Box sx={{ maxWidth: '600px', margin: '0 auto' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#00B894', mb: 2 }}>
                    How to Reserve:
                  </Typography>
                  <Box component="ol" sx={{ pl: 2, '& li': { mb: 2, fontSize: '16px', lineHeight: 1.6 } }}>
                    {fleets.length > 1 && (
                      <li>
                        <strong>Select Your Fleet:</strong> Choose the fleet location from the dropdown on the left.
                      </li>
                    )}
                    <li>
                      <strong>Pick Your Dates:</strong> Use the calendar to select your rental start and end dates.
                    </li>
                    <li>
                      <strong>Find Vehicles:</strong> Click the "Find Available Vehicles" button to see what's available for your dates.
                    </li>
                    <li>
                      <strong>Choose Your Vehicle:</strong> Browse the available options and select the vehicle type that suits your needs.
                    </li>
                    <li>
                      <strong>Review & Reserve:</strong> Review the pricing details, add any optional extras, and complete your reservation.
                    </li>
                  </Box>
                  <Box 
                    sx={{ 
                      mt: 4, 
                      p: 2, 
                      backgroundColor: 'rgba(0,184,148,0.1)', 
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="body1" sx={{ color: '#555', fontStyle: 'italic' }}>
                      Ready to get started? {fleets.length > 1 ? 'Select your fleet and dates' : 'Select your dates'} to begin!
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : availableVehicleTypes.length > 0 && !selectedVehicleType ? (
              <Box
                sx={{ 
                  width: '100%', 
                  backgroundColor: '#ffffff',
                  borderRadius: '10px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  p: isMobile ? 1 : 3,
                  border: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#333', mb: 3, textAlign: 'center' }}>
                  Available Vehicles
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {availableVehicleTypes.map((availableType) => {
                    const vehicleType = vehicleTypes.find(vt => vt.vehicle_type_id === availableType.vehicle_type_id);
                    // Use backend data if available, otherwise fall back to fleet data
                    const displayName = availableType.vehicle_type_name || vehicleType?.vehicle_type_name || 'Unknown Vehicle';
                    const vehicleImage = vehicleType?.features?.vehicle_image || '';
                    const vehicleDescription = vehicleType?.features?.vehicle_type_description || '';
                    
                    if (!vehicleType && !availableType.vehicle_type_name) return null;
                    
                    return (
                      <Box
                        key={availableType.vehicle_type_id}
                        onClick={() => handleVehicleSelection(availableType.vehicle_type_id)}
                        sx={{
                          cursor: 'pointer',
                          border: '2px solid #e0e0e0',
                          borderRadius: '12px',
                          padding: isMobile ? '9px' : '20px',
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          alignItems: isMobile ? 'stretch' : 'center',
                          gap: isMobile ? 1.5 : 3,
                          transition: 'all 0.3s ease',
                          backgroundColor: '#fff',
                          '&:hover': {
                            borderColor: '#00B894',
                            boxShadow: '0 4px 12px rgba(0,184,148,0.2)',
                            transform: isMobile ? 'none' : 'translateX(8px)'
                          }
                        }}
                      >
                        {/* Image and Arrow Container - side by side on mobile */}
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: isMobile ? 'row' : 'column',
                          alignItems: 'center',
                          gap: isMobile ? 2 : 0,
                          width: isMobile ? '100%' : 'auto'
                        }}>
                          <Box
                            sx={{
                              width: isMobile ? '120px' : '150px',
                              height: isMobile ? '80px' : '100px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <img 
                              src={vehicleImage} 
                              alt={displayName}
                              style={{ 
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                              }} 
                            />
                          </Box>
                          
                          {/* Reserve Now button - shown below image on mobile */}
                          {isMobile && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#00B894',
                                color: 'white',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                fontWeight: 600,
                                fontSize: '14px',
                                boxShadow: '0 2px 8px rgba(0,184,148,0.3)',
                                flexShrink: 0,
                                '&:hover': {
                                  backgroundColor: '#00A983'
                                }
                              }}
                            >
                              Reserve Now
                            </Box>
                          )}
                        </Box>
                        
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                              {displayName}
                            </Typography>
                            {availableType.daily_price_cents > 0 && (
                              <Typography variant="body1" sx={{ fontWeight: 500, color: '#00B894' }}>
                                ${(availableType.daily_price_cents / 100).toFixed(0)}/day
                              </Typography>
                            )}
                          </Box>
                          {vehicleDescription && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#666',
                                mb: 0.5,
                                fontStyle: 'italic'
                              }}
                            >
                              {vehicleDescription}
                            </Typography>
                          )}
                          
                          {/* Quantity dropdown - only show if more than 1 available */}
                          {availableType.available_count > 1 && (
                            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#555' }}>
                                Quantity:
                              </Typography>
                              <Select
                                value={vehicleQuantities[availableType.vehicle_type_id] || 1}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setVehicleQuantities(prev => ({
                                    ...prev,
                                    [availableType.vehicle_type_id]: e.target.value
                                  }));
                                }}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                                sx={{
                                  minWidth: '80px',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00B894'
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00A983'
                                  }
                                }}
                              >
                                {Array.from({ length: availableType.available_count }, (_, i) => i + 1).map(num => (
                                  <MenuItem key={num} value={num}>{num}</MenuItem>
                                ))}
                              </Select>
                            </Box>
                          )}
                          
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              color: availableType.available_count > 0 ? '#00B894' : '#ff5252',
                              fontWeight: 600
                            }}
                          >
                            {availableType.available_count} Available
                          </Typography>
                        </Box>
                        
                        {/* Reserve Now button - only shown on desktop (on right side) */}
                        {!isMobile && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#00B894',
                              color: 'white',
                              padding: '10px 20px',
                              borderRadius: '8px',
                              fontWeight: 600,
                              fontSize: '16px',
                              boxShadow: '0 2px 8px rgba(0,184,148,0.3)',
                              minWidth: '120px',
                              '&:hover': {
                                backgroundColor: '#00A983'
                              }
                            }}
                          >
                            Reserve Now
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : vehicleTypeError && dateRangeSelected ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{ 
                  width: '100%', 
                  textAlign: 'center', 
                  minHeight: '400px',
                  position: 'relative',
                  backgroundColor: '#ffffff',
                  borderRadius: '10px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  p: 4,
                  border: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff3cd',
                    border: '2px solid #ffc107',
                    borderRadius: '10px',
                    padding: '24px',
                    maxWidth: '500px',
                    margin: '0 auto'
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#856404',
                      fontWeight: 600,
                      marginBottom: '16px'
                    }}
                  >
                    No Vehicles Available
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#856404',
                      marginBottom: '16px',
                      lineHeight: 1.6
                    }}
                  >
                    {vehicleTypeError}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#856404',
                      fontStyle: 'italic'
                    }}
                  >
                    Please try selecting different dates or contact {fleet.account_name || 'the operator'} for assistance.
                  </Typography>
                </Box>
              </Box>
            ) : selectedVehicleType && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="flex-start"
                sx={{ 
                  width: '100%', 
                  textAlign: 'center', 
                  height: '100%',
                  position: 'relative',
                  backgroundColor: '#ffffff',
                  borderRadius: '10px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  p: isMobile ? 1 : 3,
                  border: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                {/* Vehicle image is always shown when a vehicle is selected */}
                {isMobile === false && (
                  <div style={{ 
                    width: '100%', 
                    display: 'flex', 
                    justifyContent: 'center', 
                    overflow: 'visible',
                    marginTop: '10px',
                    marginBottom: '30px'
                  }}>
                    <img
                      src={vehicleImage}
                      alt="Vehicle"
                      crossOrigin="anonymous"
                      style={{
                        height: 'auto',
                        maxWidth: '90%',
                        maxHeight: '400px',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                        padding: '10px'
                      }}
                    />
                  </div>
                )}

                <Typography
                  variant="h5" 
                  component="div" 
                  style={{ 
                    color: '#333', 
                    fontSize: isMobile ? 18 : 24,
                    fontWeight: 600,
                    textAlign: 'center',
                    marginBottom: '8px'
                  }}
                >
                  {selectedVehicleName}
                </Typography>

                {/* Rental details are conditionally shown based on date selection */}
                {dateRangeSelected && selectedStartDate && selectedEndDate && priceDetails ? (
                  <RentalDetails
                    priceDetails={priceDetails}
                    selectedQuantity={selectedQuantity}
                    selectedStartDate={selectedStartDate}
                    selectedEndDate={selectedEndDate}
                    couponPercent={couponPercent}
                    isMobile={isMobile}
                    noteContent={hasNote ? noteContent : null}
                  />
                ) : (
                  <Box
                    sx={{
                      textAlign: 'center',
                      width: '100%',
                      background: 'rgba(0,184,148,0.1)',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '16px'
                    }}
                  >
                    <Typography
                      component="div"
                      sx={{
                        fontSize: isMobile ? 16 : 18,
                        fontWeight: 500,
                        color: '#00B894',
                      }}
                    >
                      Please select a date range
                    </Typography>
                  </Box>
                )}
                
                {selectedVehicle && selectedVehicle.features && selectedVehicle.features.reservation_description && (
                  <Typography
                    component="div"
                    style={{
                      color: '#555',
                      fontSize: isMobile ? 14 : 16,
                      textAlign: 'left',
                      width: '100%',
                      padding: isMobile ? '8px' : '10px',
                      background: '#f9f9f9',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      boxSizing: 'border-box'
                    }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedVehicle.features.reservation_description) }}
                  />
                )}                <Button
                  onClick={handleReserveClick}
                  variant="contained"
                  color="primary"
                  sx={{
                    backgroundColor: '#00B894',
                    color: 'white',
                    borderRadius: '30px',
                    height: '48px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    outline: 'none',
                    border: 'none',
                    alignSelf: 'center',
                    marginTop: '16px',
                    padding: '0 32px',
                    fontSize: '16px',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(0,184,148,0.3)',
                    '&:hover': {
                      backgroundColor: '#00A983',
                      boxShadow: '0 6px 16px rgba(0,184,148,0.4)',
                    },
                  }}
                >                  RESERVE NOW
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      <LoginDialog
        open={isLoginDialogOpen}
        onClose={() => setIsLoginDialogOpen(false)}
        title="To finalize this booking, please verify your mobile number"
        successCallback={async ({ user, user_id, waiver_status, stripe_customer_id, operator, loadStripe }) => {
          try {
            // Safety check for priceDetails
            if (!priceDetails) {
              console.error('priceDetails is not available - this should not happen');
              throw new Error('Pricing information is not available. Please try selecting your dates again.');
            }
            
            const fleet = JSON.parse(localStorage.getItem('fleet'));

            // Create simplified params object for new API
            // Backend now handles: lookups, hash generation, pricing calculation, receipt creation, success_url
            const params = {
              amount: priceDetails.amount, // Total amount for validation
              user_id,
              fleet_id: fleet.id,
              vehicle_type_id: selectedVehicleType,
              start_date: startDate,
              end_date: endDate,
              coupon_id: hasCoupon ? couponId : null,
              addon_ids: selectedAddons.length > 0 ? selectedAddons : null,
              note: noteContent || null,
              quantity: selectedQuantity
            };

            // Store minimal params in localStorage for potential retry
            const reservationParams = {
              params,
              stripe_pk: operator.stripe_pk
            };

            localStorage.setItem('reservationParams', JSON.stringify(reservationParams));
            
            // Check if operator has enhanced_waiver feature AND waiver_status is null or rejected
            if (operator.features && operator.features.includes('enhanced_waiver') && (waiver_status === null || waiver_status === 'rejected')) {
              setIsLoginDialogOpen(false);
              navigate(`/waiver?user_id=${user_id}&operator=${operator.id}&fleet_id=${fleet.id}&payment_flow=true`);
            } else {
              try {
                const session = await createCheckoutSession(params);
                
                const stripe = await loadStripe(operator.stripe_pk);
                console.log('Stripe loaded, redirecting to checkout');
                
                const { error } = await stripe.redirectToCheckout({ sessionId: session.id });
                if (error) {
                  console.error('Stripe redirect error:', error);
                  throw new Error('Failed to redirect to payment. Please try again.');
                }
              } catch (checkoutError) {
                // Handle specific error cases from the new API
                if (checkoutError.message && checkoutError.message.includes('Price Mismatch')) {
                  console.error('Price mismatch detected:', checkoutError);
                  throw new Error('The pricing has changed. Please review the updated price and try again.');
                } else if (checkoutError.message && checkoutError.message.includes('User Not Found')) {
                  throw new Error('User account not found. Please try logging in again.');
                } else if (checkoutError.message && checkoutError.message.includes('Fleet Not Found')) {
                  throw new Error('Fleet information not found. Please refresh and try again.');
                } else if (checkoutError.message && checkoutError.message.includes('Vehicle Type Not Found')) {
                  throw new Error('Selected vehicle type is no longer available. Please select a different vehicle.');
                } else {
                  console.error('Checkout error:', checkoutError);
                  throw checkoutError;
                }
              }
            }
          } catch (error) {
            console.error('Error in reservations success callback:', error);
            // The LoginDialog component will handle this error and show it to the user
            throw error;
          }
        }}
      />
    </div>
  );
};

export default ReservationPage;
